/**
 * Picks the file for a box from a srcset the server printed ("address 320w, address 720w,
 * ..."): the smallest candidate at least as wide as the box in device pixels, else the
 * largest one. The inline viewer of the ajax layout takes the file of its stage this way
 * from the lightbox srcset of the thumbnail; the browser does the same for an <img> by
 * itself. Until 7.0.0 the scripts rebuilt the address of a size per storage
 * (EventgallerySizeCalculator.js) and never looked at the pixel density.
 *
 * @param {string|null} srcset the srcset with w descriptors
 * @param {number} width the width of the box in CSS pixels
 * @param {number} [pixelRatio] the device pixel ratio, window.devicePixelRatio when left out
 * @returns {string|null} the address, null for an empty srcset
 */
export function pickFromSrcSet(srcset, width, pixelRatio) {
    const candidates = parseSrcSet(srcset);
    if (candidates.length === 0) {
        return null;
    }
    const ratio = pixelRatio || (typeof window !== 'undefined' && window.devicePixelRatio) || 1;
    const needed = Math.ceil(width * ratio);
    const fitting = candidates.find((candidate) => candidate.width >= needed);

    return (fitting || candidates[candidates.length - 1]).url;
}

/**
 * @param {string|null} srcset
 * @returns {{url: string, width: number}[]} the candidates, ascending by width
 */
export function parseSrcSet(srcset) {
    if (!srcset) {
        return [];
    }

    return String(srcset)
        .split(',')
        .map((entry) => entry.trim())
        .filter((entry) => entry !== '')
        .map((entry) => {
            const parts = entry.split(/\s+/);
            const descriptor = parts.length > 1 ? parts[parts.length - 1] : '';
            const width = /^\d+w$/.test(descriptor) ? parseInt(descriptor, 10) : 0;
            return { url: parts[0], width };
        })
        .filter((candidate) => candidate.width > 0)
        .sort((a, b) => a.width - b.width);
}

/**
 * @param {HTMLImageElement} img
 * @returns {number} the width of the candidate of its srcset the image shows or loads
 *          (currentSrc), 0 when it has none of them yet
 */
export function currentWidth(img) {
    const current = img.currentSrc;
    if (!current) {
        return 0;
    }
    const candidate = parseSrcSet(img.getAttribute('srcset')).find((entry) => entry.url === current || absolute(entry.url) === current);

    return candidate ? candidate.width : 0;
}

/**
 * The candidates of a srcset which are narrower than the one which did not load: what the
 * lightbox tries next when a large file is refused (Flickr's CDN answers 429 for the sizes
 * from 1600 px to some visitors; photoswipe/LightboxImageFallback.js). The failed one is
 * found by its address; one which is not in the srcset (the src of the image) counts with
 * the width the caller knows.
 *
 * @param {string|null} srcset the srcset with w descriptors
 * @param {string} failedUrl the address which did not load
 * @param {number} [failedWidth] the width of a failed address which is not in the srcset
 * @returns {{srcset: string, src: string}|null} the smaller candidates and the largest of
 *          them as the src, or null when there is no smaller one
 */
export function srcSetBelow(srcset, failedUrl, failedWidth = Infinity) {
    const candidates = parseSrcSet(srcset);
    const failed = candidates.find((candidate) => candidate.url === failedUrl || absolute(candidate.url) === failedUrl);
    const limit = failed ? failed.width : failedWidth;
    const smaller = candidates.filter((candidate) => candidate.width < limit);
    if (smaller.length === 0) {
        return null;
    }

    return {
        srcset: smaller.map((candidate) => candidate.url + ' ' + candidate.width + 'w').join(', '),
        src: smaller[smaller.length - 1].url,
    };
}

/**
 * @param {string} url
 * @returns {string} the address resolved against the page, as currentSrc reports it
 */
function absolute(url) {
    try {
        return new URL(url, typeof document !== 'undefined' ? document.baseURI : undefined).href;
    } catch (e) {
        return url;
    }
}
